import {_} from 'lodash';
import * as ko from "knockout";
import {
    IExportTemplate,
    IFormTemplate,
    IMessage,
    IMessageData,
    IObjectData,
    IRuleset,
    ISystem,
    ISystemRole,
    TRefKey
} from '../../../index';
import {global} from "../../../appContext";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsReference} from "../../../util/nsReference";
import {getSearchParams, getSearchSystemParams} from "../../../util/request";
import {HIERARCHY_ARROW, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../constants";
import {getObjectDefinition, getObjectDisplayNameFn, IObjectDefinition} from "../../../util/misc";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {nsRuleset} from "../nsRuleset";
import {getHash, getRouteHash} from "../../../constants/RouteDefinitions";
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

interface IParams {
    ruleset: IRuleset
}

interface IReferenceSection {
    $def: nsReference.IReferenceDefinition
    refs: KnockoutObservableArray<IReferenceObject>
}

interface IReferenceObject {
    $def: nsReference.IReferenceDefinition
    href: string
    id: string
    name: string | null
    total: KnockoutObservable<number>
}

export class viewModel {
    private data: KnockoutObservableArray<IReferenceSection> = ko.observableArray<IReferenceSection>([]);
    private isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly req: PromiseRequest = global.getPromiseRequest('searchexporttemplate');
    private readonly ruleset: IRuleset;
    private readonly requests: PromiseRequest[] = [];

    constructor(params: IParams) {
        this.ruleset = params.ruleset;
        this.init();
    }

    private init() {
        this.messageData({message: REQUEST_MESSAGE});
        this.isReady(true);

        _.forEach(this.ruleset.refs, (val: string[], key: TRefKey) => {
            let $def = nsReference.getReferenceDefinition(key);
            let k: TRefKey = $def.group || $def.key;
            $def = nsReference.getReferenceDefinition(k);

            if (key == 'jruleset') {
                val = nsRuleset.getRefsWithoutReferenceToSubRulesets(this.ruleset) ?? val
            }

            const refIds = val ?? [];
            if (!refIds.length) {
                return
            }

            let section = this.data().find(o => o.$def.key == k);
            if (!section) {
                section = {
                    $def: $def,
                    refs: ko.observableArray<IReferenceObject>([]),
                }
                this.data.push(section);
            }
            const sectionRef = section;

            refIds.forEach(id => {
                this.getReferenceObject(id, key)
                    .then((o => {
                        const existing = sectionRef.refs().find(s => s.id == o.id);
                        if (existing) {
                            existing.total(existing.total() + 1);
                        } else {
                            sectionRef.refs.push(o);
                            o.total(1);
                        }
                    }));
            })
        });

        Promise.all(this.requests.map(o => o.promise))
            .then(() => {
                this.messageData(null);
            });
    }

    private getReferenceObject(id: string, key: TRefKey): Promise<IReferenceObject> {
        const refObj: nsReference.IReferenceDefinition = nsReference.getReferenceDefinition(key);
        const href = refObj.routeId ? (getHash(refObj.routeId, {id: id}) ?? '') : ''
        const ret: IReferenceObject = {
            $def: refObj,
            href: href,
            id: id,
            name: null,
            total: ko.observable(0),
        }
        return Promise
            .resolve()
            .then(() => {
                switch (refObj.key) {
                    case 'jcompany': {
                        ret.name = '#' + id;
                        return ret;
                    }
                    case 'jcompanysyncstep': {
                        ret.name = '#' + id;
                        return ret;
                    }
                    case 'jexportflow': {
                        return this.getExportTemplates()
                            .then((data: IExportTemplate[]) => data.find(et => et.exportflow.find(ef => String(ef.id) == id)))
                            .then((o: IExportTemplate | undefined) => {
                                if (!o) {
                                    return ret;
                                }
                                ret.id = String(o.id);
                                ret.href = refObj.routeId ? (getHash(refObj.routeId, {id: o.id}) ?? ret.href) : ret.href;
                                ret.name = o.name;
                                return ret;
                            });
                    }
                    case 'jexporttemplate': {
                        return this.getExportTemplate(id)
                            .then((o: IExportTemplate) => {
                                ret.name = o.name;
                                return ret;
                            });
                    }
                    case 'jformtemplate': {
                        return this.getFormTemplate(id)
                            .then((o: IFormTemplate) => {
                                ret.name = o.name
                                return ret
                            })
                    }
                    case 'jmanualobject': {
                        return this.getManualObjectReference(id, true, key);
                    }
                    case 'jmessage': {
                        return this.getMessage(id)
                            .then((o: IMessage) => {
                                ret.name = o.name;
                                return ret;
                            });
                    }
                    case 'jobject': {
                        return this.getObjectReference(id, key);
                    }
                    case 'jruleset': {
                        return this.getRuleset(id)
                            .then((o: IRuleset) => {
                                ret.name = o.name;
                                return ret;
                            });
                    }
                    case 'jsystem': {
                        return this.getSystems()
                            .then((data: ISystem[]) => data.find(sys => String(sys.id) == id))
                            .then((o: ISystem | undefined) => {
                                if (!o) {
                                    return ret;
                                }
                                ret.name = o.name;
                                return ret;
                            })
                    }
                    case 'jsystemrole': {
                        let role: ISystemRole | undefined;
                        return this.getSystems()
                            .then((data: ISystem[]) => data.find(sys => role = sys.roles.find(r => String(r.id) == id)))
                            .then((o: ISystem | undefined) => {
                                if (!o || !role) {
                                    return ret;
                                }
                                ret.id = String(role.id);
                                ret.href = refObj.routeId ? (getHash(refObj.routeId, {id: o.id}) ?? ret.href) : ret.href;
                                ret.name = `${o.name} ${HIERARCHY_ARROW} ${role.name}`;
                                return ret;
                            });
                    }
                    default: {
                        return Promise.resolve(ret);
                    }
                }
            })
    }

    private getExportTemplate(id: string): Promise<IExportTemplate> {
        const req = global.getPromiseRequest('getexporttemplate');
        this.requests.push(req);
        return req.request({id})
            .then((data: IExportTemplate[]) => {
                const first = data[0];
                if (!first) {
                    throw new Error(`Missing export template: ${id}`);
                }
                return first;
            });
    }

    private getExportTemplates(): Promise<IExportTemplate[]> {
        const req = global.getPromiseRequest('searchexporttemplate');
        this.requests.push(req);
        return req
            .request(getSearchParams({
                resultsprpage: MAX_RESULTS_PER_PAGE,
                sortcol: 'name',
            }));
    }

    private getFormTemplate(id: string): Promise<IFormTemplate> {
        const req = global.getPromiseRequest('getformtemplate');
        this.requests.push(req);
        return req
            .request({id})
            .then((data: IFormTemplate[]) => {
                const first = data[0];
                if (!first) {
                    throw new Error(`Missing form template: ${id}`);
                }
                return first;
            });
    }

    private getManualObjectReference(id: string, manual: boolean, key: TRefKey): Promise<IReferenceObject> {
        const req = global.getPromiseRequest('getobject');
        this.requests.push(req);
        return req
            .request({
                id: id,
                manual: manual,
            })
            .then((data: IObjectData[]) => {
                const first = data[0];
                if (!first) {
                    throw new Error(`Missing object: ${id}`);
                }
                return first;
            })
            .then((o: IObjectData) => {
                const $def = nsReference.getObjectReferenceDefinition(key, o.type.id, manual);
                const objDef = getObjectDefinition(o.type.id);
                const routeId = o.manual ? objDef?.routeDetailsManual : objDef?.routeDetails;
                const hash = routeId ? getHash(routeId, {id: id}) ?? '' : '';
                return {
                    $def: $def,
                    href: hash,
                    id: id,
                    name: getObjectDisplayNameFn(o.type.id)(o),
                    total: ko.observable(0),
                }
            });
    }

    private getObjectReference(id: string, key: TRefKey) {
        return Promise
            .resolve()
            .then(() => {
                let objectTypeId: ObjectTypeId;
                let objDef: IObjectDefinition;
                const req = global.getPromiseRequest('evaluateexpression');
                this.requests.push(req);
                return req
                    .request({
                        expression: '[objecttypeid]',
                        objectid: id,
                    })
                    .then((data: string[]) => {
                        const first = data[0];
                        if (!first) {
                            throw new Error(`Missing objecttypeid for object ${id}`);
                        }
                        objectTypeId = Number(first);
                        objDef = _.cloneDeep(getObjectDefinition(objectTypeId));
                        return (() => {
                            const sf = objDef.syncfieldDisplayName;
                            if (!sf?.length) {
                                return '';
                            }
                            const last = sf.at(-1);
                            if (!last) {
                                return '';
                            }
                            let ret: string = `[${last.toLowerCase()}]`;
                            const remaining = sf.slice(0, -1);
                            while (remaining.length) {
                                ret = `[IFEMPTYUSE;[${remaining.pop()!.toLowerCase()}];${ret}]`;
                            }
                            return ret;
                        })();
                    })
                    .then((expression: string) => {
                        const req = global.getPromiseRequest('evaluateexpression');
                        this.requests.push(req);
                        return req
                            .request({
                                expression: expression,
                                objectid: id,
                            })
                            .then((data: string[]) => {
                                const routeId = objDef.routeDetails;
                                return {
                                    $def: nsReference.getObjectReferenceDefinition(key, objectTypeId, false),
                                    href: routeId ? getHash(routeId, {id: id}) ?? '' : '',
                                    id: id,
                                    name: data[0] ?? '',
                                    total: ko.observable(0),
                                };
                            })
                    })
            });
    }

    private getMessage(id: string): Promise<IMessage> {
        const req = global.getPromiseRequest('getmessage');
        this.requests.push(req);
        req.abort()
        return req
            .request({id: id})
            .then((data: IMessage[]) => {
                const first = data[0];
                if (!first) {
                    throw new Error(`Missing message: ${id}`);
                }
                return first;
            });
    }

    private getSystems(): Promise<ISystem[]> {
        const req = global.getPromiseRequest('searchsystem');
        this.requests.push(req);
        return req
            .request(getSearchSystemParams({
                resultsprpage: MAX_RESULTS_PER_PAGE,
                sortcol: 'name',
            }));
    }

    private getRuleset(id: string): Promise<IRuleset> {
        const req = global.getPromiseRequest('getruleset');
        this.requests.push(req);
        return req
            .request({id: id})
            .then((data: IRuleset[]) => {
                const first = data[0];
                if (!first) {
                    throw new Error(`Missing ruleset: ${id}`);
                }
                return first;
            });
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req.abort();
        this.requests.forEach(p => p.abort());
    }

}
